// Invoice history for one family, across every subscription row it has held.
//
// 🔴 NO BLENDED TOTAL, EVER. A family can hold invoices in more than one
// currency (a plan change re-prices, and we sell in seven currencies with no FX
// table anywhere in this system). A single "total paid" figure would therefore
// have to invent an exchange rate. So amounts are summed PER CURRENCY via
// `sumMinor()`, which throws rather than add QAR to KWD, and the paid total is
// rendered as one line per currency.
//
// 🔴 Read-only. `invoices` is part of the append-only money ledger: a refund is
// a separate row (apps/api/src/lib/refunds.ts), never an edit to the invoice it
// reverses. There is deliberately no row action that mutates an invoice here.

import { useMemo } from 'react';

import { DataTable, type Column } from '../DataTable';
import { Money } from '../Money';
import { StatusBadge } from '../StatusBadge';

import type { FamilyInvoice } from '~/lib/admin';
import { groupByCurrency, sumMinor } from '~/lib/money';

export function FamilyInvoices({
  invoices,
  loading,
  error,
  onRetry,
}: {
  invoices: FamilyInvoice[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  // Only PAID invoices count toward "what this customer has actually paid us".
  // A draft or a failed invoice is a claim, not money.
  const paidByCurrency = useMemo(() => {
    const paid = invoices.filter((i) => i.status === 'paid');
    return groupByCurrency(paid).map(({ currency, rows }) => ({
      currency,
      // Safe by construction: groupByCurrency guarantees a single currency per
      // group, which is exactly the precondition sumMinor enforces.
      total: sumMinor(rows)?.amountMinor ?? 0,
      count: rows.length,
    }));
  }, [invoices]);

  const columns: Column<FamilyInvoice>[] = [
    {
      key: 'issuedAt',
      header: 'Issued',
      sortable: true,
      alwaysVisible: true,
      render: (i) => <span className="text-admin-700">{formatDate(i.issuedAt)}</span>,
    },
    {
      key: 'planSlug',
      header: 'Plan',
      render: (i) =>
        i.planSlug ? (
          <span className="text-admin-700">{i.planSlug}</span>
        ) : (
          // `invoices.plan_id` is ON DELETE SET NULL, so a deleted plan leaves
          // a real invoice with no slug. That is history, not an error.
          <span className="text-admin-300" title="The plan this billed for no longer exists">
            —
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (i) => <StatusBadge status={i.status} dot />,
    },
    {
      key: 'discountMinor',
      header: 'Discount',
      align: 'end',
      hideBelow: 'md',
      render: (i) =>
        i.discountMinor > 0 ? (
          <Money amountMinor={i.discountMinor} currency={i.currency} emphasis="muted" />
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'amountMinor',
      header: 'Amount',
      sortable: true,
      align: 'end',
      alwaysVisible: true,
      render: (i) => <Money amountMinor={i.amountMinor} currency={i.currency} emphasis="strong" />,
    },
    {
      key: 'paidAt',
      header: 'Paid',
      sortable: true,
      align: 'end',
      hideBelow: 'lg',
      render: (i) =>
        i.paidAt ? (
          <span className="text-xs text-admin-500">{formatDate(i.paidAt)}</span>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
  ];

  return (
    <div>
      <DataTable
        columns={columns}
        rows={invoices}
        rowKey={(i) => i.id}
        loading={loading}
        error={error}
        onRetry={onRetry}
        caption="Invoices"
        empty={{
          icon: 'receipt',
          title: 'No invoices',
          description: 'Nothing has been billed to this family yet.',
        }}
      />

      {paidByCurrency.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-sm">
          {/* One line PER CURRENCY. Never a single combined figure — see the
              file header. Labelling each with its count makes it obvious that
              these are separate tallies rather than a split of one total. */}
          {paidByCurrency.map((g) => (
            <span key={g.currency} className="text-admin-600">
              <span className="text-xs uppercase tracking-wide text-admin-400">
                Paid ({g.count})
              </span>{' '}
              <Money amountMinor={g.total} currency={g.currency} emphasis="strong" />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
